// apps/widget/src/lib/summary.ts
//
// The report's summary, written from this person's own scores.
//
// Built in the widget from numbers already on the screen, so it can never
// disagree with them, and it says something different for every scan: the
// band, the strongest and weakest areas by name and score, and care for the
// skin type. A fixed paragraph that every visitor sees word for word reads as a
// template, and patients notice.
//
// Sentences are phrased around "areas" ("Your strongest areas are …") so the
// verb never has to agree with a metric label — "Spots" is plural, "Firmness"
// is not, and that agreement differs again in every locale.

import type { Report, ReportMetric } from './api';
import { t, metricLabel } from './i18n';

const KNOWN_TYPES = ['dry', 'oily', 'combination', 'normal', 'sensitive'];

export function buildSummary(report: Report): string[] {
  const ranked = [...report.metrics].sort((a, b) => b.score - a.score);
  const strengths = ranked.filter((m) => m.band === 'good').slice(0, 2);
  // Weakest first; only scores below "good" count as something to work on.
  const concerns = [...ranked].reverse().filter((m) => m.band !== 'good').slice(0, 2);

  const overview = [t(`summary.overall.${report.band}`, { score: report.skinScore })];

  if (strengths.length > 0) {
    overview.push(t('summary.strengths', { list: list(strengths) }));
  }
  overview.push(
    concerns.length > 0
      ? t('summary.concerns', { list: list(concerns) })
      : t('summary.noConcerns'),
  );

  const paragraphs = [overview.join(' ')];

  if (KNOWN_TYPES.includes(report.skinType)) {
    paragraphs.push(
      t('summary.skinType', { type: t(`skinTypeName.${report.skinType}`).toLowerCase() }) +
        ' ' +
        t(`skinTypeCare.${report.skinType}`),
    );
  }

  return paragraphs;
}

function list(metrics: ReportMetric[]): string {
  const items = metrics.map((m) => `${metricLabel(m.key, m.label)} (${m.score})`);
  return items.length === 2 ? t('summary.and', { a: items[0]!, b: items[1]! }) : items[0]!;
}
